import moment from 'moment/moment';
import { Task, TaskStatus, WayPoint } from '@bringg/types';
import { TimelineItem } from '@bringg-frontend/bringg-timeline';

import styles from './task-status.module.scss';

export enum OnTimeStatus {
	late = 'late',
	early = 'early',
	error = 'error'
}

export const taskStyles = styles;
export const getTaskStatusClass = (task: Task, etaAppInstalled: boolean): string => {
	if (task.status === TaskStatus.Cancelled) {
		return styles.canceledTask;
	}

	if (!etaAppInstalled) {
		if (task.status === TaskStatus.Done) {
			return styles.completedTask;
		}
		return styles.futureTask;
	}

	const status = getTaskStatus(task);

	if (task.status === TaskStatus.Done) {
		switch (status) {
			case OnTimeStatus.late:
				return styles.completedLateTask;

			case OnTimeStatus.early:
				return styles.completedEarlyTask;
			default:
				return styles.completedTask;
		}
	}

	const inProgress = task.status === TaskStatus.CheckedIn;

	switch (status) {
		case OnTimeStatus.late:
			return inProgress ? styles.inProgressLateTask : styles.futureLateTask;
		case OnTimeStatus.early:
			return inProgress ? styles.inProgressEarlyTask : styles.futureTask;
		default:
			return inProgress ? styles.inProgressOnTimeTask : styles.futureTask;
	}
};

export const getClusterStatusClass = (clusterItem: TimelineItem[]) => {
	const statusByPriority = [
		'futureLateTask',
		'inProgressLateTask',
		'completedLateTask',
		'completedEarlyTask',
		'canceledTask'
	];

	const statusClasses = [...new Set(clusterItem.map(item => item.className))].filter(Boolean) as string[];

	if (statusClasses.length === 1) {
		return statusClasses[0];
	}

	for (const status of statusByPriority) {
		if (statusClasses.find(item => item.indexOf(status) > -1)) {
			return styles[status];
		}
	}
	return styles.futureTask;
};

const isEarly = (arrivalTime: string, noEarlierThan?: string): boolean => {
	if (!noEarlierThan) {
		return false;
	}
	return moment(arrivalTime).isBefore(noEarlierThan, 'minutes');
};

export const getTaskStatus = (task: Task): OnTimeStatus => {
	const wayPoint: WayPoint = (task.way_points || []).reduce((lastWayPoint, wp) => {
		if (!wp.position) return lastWayPoint;
		if (!lastWayPoint.position) return wp;

		return lastWayPoint.position > wp.position ? lastWayPoint : wp;
	});

	if (task.late) {
		return OnTimeStatus.late;
	}

	if (!wayPoint || !wayPoint.eta) {
		return OnTimeStatus.error;
	}

	let time = wayPoint.eta;
	if (wayPoint.checkin_time) {
		time = wayPoint.checkin_time;
	}

	if (isEarly(time, wayPoint.no_earlier_than || wayPoint.first_attempt_promise_no_earlier_than))
		return OnTimeStatus.early;

	return OnTimeStatus.error;
};

const taskStartedStatuses = new Set([TaskStatus.OnTheWay, TaskStatus.CheckedIn, TaskStatus.Done, TaskStatus.Cancelled]);

export const tasksReducerByStartStatus = (tasks: Task[]) => {
	const { started: startedTaskIds, unStarted: unStartedTasksIds } = tasks.reduce(
		({ started, unStarted }, task) => {
			if (task.status) {
				if (taskStartedStatuses.has(task.status)) {
					started.push(task.id as number);
				} else {
					unStarted.push(task.id as number);
				}
			}

			return { started, unStarted };
		},
		{ started: [], unStarted: [] } as { started: number[]; unStarted: number[] }
	);

	return { startedTaskIds, unStartedTasksIds };
};
